import React from 'react';

import { ButtonGroup } from 'components/ui/button'
import { Container, Content, Wrap, ItemIndex, DragHandle } from 'components/ui/draggable'
import { IconHash, IconLanguages, IconAudioTrack, IconRoom, IconWarning, IconOverride } from 'components/ui/icons'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { Description, Image, Title, Subtitle } from 'components/ui/list-item-label'

import { stopSchema } from 'domains/org/getty/schemas/stop'
import { StopWithSpace } from 'domains/org/getty/store'

import { AttributeRef } from 'generated/mos/entity'
import { Stop } from 'generated/mos/org/getty/audio'
import { Media } from 'generated/mos/media'

import { Invariant } from 'helpers/core'
import { space } from 'helpers/style'

import styled from 'styled'

const IconBlock = styled.div`
  display: flex;
  align-items: center;
  margin: 0 10px;
  font-weight: ${({ theme }) => theme.font.weight.base};
  color: ${({ theme }) => theme.color.grayL40};
`;

const IconText = styled.span<{ wide?: boolean }>`
  width: ${({ wide }) => wide ? space(45) : space(10)};
  margin-left: ${space(2)};
  font-size: ${({ theme }) => theme.font.size.small};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Warning = styled.div`
  width: 20px;
`

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 0;
`

const DisplayBadge = styled.div`
  white-space: nowrap;
`;

const Badge = styled.div<{ red?: boolean }>`
  display: inline-block;
  margin-right: ${space(3)};
  padding: 1px 2px;
  border: 1px solid ${({ theme, red }) => red ? theme.color.error : theme.color.grayL20};
  border-radius: 2px;
  font-size: 10px;
  color: ${({ theme, red }) => red ? theme.color.error : theme.color.gray};
  font-weight: ${({ theme }) => theme.font.weight.light};
  text-transform: uppercase;
`

type Props = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly stop: StopWithSpace;
  readonly addToTour?: () => void;
  readonly deleteFromTour?: (stopRef: Stop.Ref) => void;
  readonly tourId?: string;
  readonly draggableIndex?: number;
  readonly placement?: string;
  readonly site?: string;
  readonly stopsListItemLink?: boolean;
  readonly isDragging?: boolean;
  readonly hasWarning?: boolean;
  readonly hostName?: string;
  readonly hasAtLeastOneOverride?: boolean;
};

export const ListItem: React.FC<Props> = ({
  addToTour,
  deleteFromTour,
  draggableIndex,
  historyPush,
  isDragging,
  projectId,
  stop,
  placement,
  tourId,
  stopsListItemLink,
  hasWarning,
  site,
  hostName,
  hasAtLeastOneOverride,
}) => {
  const stopId = Stop.mustRef(stop.ref).id
  // if we are viewing a tour, maintain that context in the url,
  // otherwise treat as random access stop.
  const editLocation = tourId ? `/project/${projectId}/tour/${tourId}/stop/${stopId}` : `/project/${projectId}/stop/${stopId}`

  let title: any = stopSchema.title.read(stop.title)
  let producedBy: any = stopSchema.producedBy.read(stop.producedBy)

  const stopImage = stopSchema.image.read(stop.image) as Media.Ref
  const stopImageId = stopImage && stopImage.id

  const imagePath = stopImageId && hostName ? `${hostName}/images/${stopImageId}.jpg?s=crop&h=92&w=92` : ''

  // check if Attribute ref resolved to a string value
  if (title && title.type === AttributeRef.refName) {
    title = ''
  }
  // check if Attribute ref resolved to a string value
  if (producedBy && producedBy.type === AttributeRef.refName) {
    producedBy = undefined
  }

  return(
    <Container isDragging={isDragging} onClick={() => {stopsListItemLink && historyPush(editLocation)}}>
      <Wrap isDragging={isDragging} stopsListItemLink={stopsListItemLink}>
        <Content>
          {draggableIndex !== undefined && (
            <>
              <DragHandle />
              <ItemIndex>
                {draggableIndex + 1}
              </ItemIndex>
            </>
          )}
          <Image src={imagePath} />
          <Description>
            <Title>{title}</Title>
            <DisplayBadge>
              {stop.basedOnRef && <Badge red={!placement}>{placement ? "on display" : "not on display"}</Badge>}
              <Subtitle>{`${site ? `${site} - ` : ''}${producedBy ? producedBy : '-'}`}</Subtitle>
            </DisplayBadge>
          </Description>
        </Content>
        <IconsContainer>
          <IconBlock>
            <IconHash color="grayL40" size="small" />
            <IconText>{stop.wallNumber ? stop.wallNumber : '-'}</IconText>
          </IconBlock>
          <IconBlock>
            <IconAudioTrack color="grayL40" size="small" />
            <IconText>{stop.audio ? 'M4A' : '-'}</IconText>
          </IconBlock>
          <IconBlock>
            <IconRoom color="grayL40" size="small" />
            <IconText wide>{placement ? placement : '-'}</IconText>
          </IconBlock>
          {
            hasAtLeastOneOverride && (
              <IconBlock>
                <div style={{ paddingTop: '10px' }}>
                  <IconOverride size={15} />
                </div>
              </IconBlock>
            )
          }
          <IconBlock>
            <Warning>{hasWarning && <IconWarning size="small" color="warning" />}</Warning>
          </IconBlock>
        </IconsContainer>
        <PopOverMenu iconSize="base">
          <PopOverMenuButton
            onClick={(event) => {
              historyPush(editLocation)
              event.stopPropagation()
            }}
          >
            Edit
          </PopOverMenuButton>
          {addToTour && (
            <PopOverMenuButton onClick={(event) => {
              addToTour()
              event.stopPropagation()
            }}>
              Add to Tour
            </PopOverMenuButton>
          )}
          {deleteFromTour && (
            <PopOverMenuButton onClick={() => {
                const stopRef = Stop.mustRef(stop.ref)
                deleteFromTour(stopRef)
              }
            }>
              Remove from Tour
            </PopOverMenuButton>
          )}
        </PopOverMenu>
      </Wrap>
    </Container>
  )
}
